import React, { ChangeEvent, FC, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Paper } from '@material-ui/core';
import { DateTime } from 'luxon';
import {
  EventStatsGraphWrapper,
  EventStatsHeaderDiv,
  GraphWrapper,
  TabsDiv,
  DropDownWrapper,
  IconWrapper,
  ChartControls,
  InfoBoxWrapper,
} from './youtuber-stats-graph.style';
import Link from '@material-ui/core/Link';
import Tooltip from '@material-ui/core/Tooltip';
import { ExportCSVWrapper, LinkButtonContainer } from '../../event-analytics.styles';
import { Select, Grid, Text, Option, Inline } from '@resi-media/resi-ui';
import {
  ClockCircleOutlined,
  ExportOutlined,
  TeamOutlined,
  UndoOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons';
import { GROUP_BY_VALUE, lineChartDataType, SelectOption, TIME_WINDOWS, YOUTUBE_PAGES } from '../../types';
import Modal from '../../../../components/Modal';
import ModalHeader from '../../../../components/ModalHeader';
import { DateRange, Range } from 'react-date-range';
import { renameKey } from '../../utils';
import {
  DESTINATION_TYPES,
  LEGEND_NAMES,
  LOADER_SIZE_COMPONENT,
  LOADER_SIZE_SUB_COMPONENT,
  PAGE_VALUE,
  TOOLTIP_LABEL,
  ZOOM_CTRL_TOOLTIP,
} from '../../event-analytics-constants';
import {
  ContainedButton,
  ContainedTab,
  ContainedTabs,
  FillBlockSpinner,
  InfoBox,
  LineChart,
  NavTab,
  NavTabs,
  Surface,
  ViewEvents,
} from '../../../../components';
import { EventAnalyticsContext } from '../../../../contexts/eventanalytics/event-analytics';
import { LIFE_TIME_DATE_LOCAL, START_DATE_LOCAL } from '../../date-range-options';
// DateRangeSelector CSS dependencies
import 'react-date-range/dist/styles.css'; // main style
import 'react-date-range/dist/theme/default.css'; // theme css file
// END

interface YoutubeGraphViewProps {
  data: lineChartDataType;
  dateRangeOptions: SelectOption[];
  isChartLoading: boolean;
  isDatePickerOpen: boolean;
  timeWindow: number;
  dataType: number;
  dataPage: number;
  dataPageName: string;
  handleGroupByChange: (event: ChangeEvent<unknown>, newValue: GROUP_BY_VALUE) => void;
  handleDataPageChange: (event: ChangeEvent<unknown>, newValue: YOUTUBE_PAGES) => void;
  handleDateRangeChange: (item: Option[]) => void;
  handleTimeWindowChange: (event: ChangeEvent<unknown>, newValue: TIME_WINDOWS) => void;
  handleCustomDateRangeSelection: (startDate: string, endDate: string) => void;
  closeDatePicker: () => void;
  exportCSV: () => void;
  isExportCSVLoading: boolean;
}

const YoutubeStatsGraphView: FC<YoutubeGraphViewProps> = ({
  data,
  dateRangeOptions,
  isChartLoading,
  isDatePickerOpen,
  timeWindow,
  dataType,
  dataPage,
  dataPageName,
  handleGroupByChange,
  handleDataPageChange,
  handleDateRangeChange,
  handleTimeWindowChange,
  handleCustomDateRangeSelection,
  exportCSV,
  closeDatePicker,
  isExportCSVLoading,
}): JSX.Element => {
  const {
    EventAnalyticsState: {
      viewAllData,
      dateRange: { startDate, endDate },
      eventId,
      dateRangeOption,
    },
  } = useContext(EventAnalyticsContext);
  const [selectionRange, setSelectionRange] = useState<Range>({
    startDate: new Date(startDate),
    endDate: new Date(endDate),
    key: 'selection',
  });
  const [isInfoBoxDisplayed, setInfoBoxDisplayStatus] = useState<boolean>(true);
  const disableTab = dataPageName !== PAGE_VALUE.LIVE_VIEWS ? true : false;
  const disableDeviceTab = dataPageName === PAGE_VALUE.AVG_WATCH_TIME ? true : false;

  const handleDateRangeSelect = (range: { selection: Range }) => {
    const { selection } = range;
    const { hour, minute, second, millisecond } = DateTime.local().toObject();
    setSelectionRange({
      startDate: selection.startDate,
      endDate: selection.endDate,
      key: 'selection',
    });
    if (selectionRange.startDate && selectionRange.endDate) {
      const selectionStartDate = selection.startDate
        ? DateTime.fromJSDate(selection.startDate)
            .plus({
              hours: hour,
              minutes: minute,
              seconds: second,
              milliseconds: millisecond,
            })
            .toISO() || ''
        : '';
      const selectionEndDate = selection.endDate
        ? DateTime.fromJSDate(selection.endDate)
            .plus({
              hours: hour,
              minutes: minute,
              seconds: second,
              milliseconds: millisecond,
            })
            .toISO() || ''
        : '';
      handleCustomDateRangeSelection(selectionStartDate, selectionEndDate);
    }
  };

  Object.keys(data.datasets).map((el) => {
    if (el in LEGEND_NAMES) {
      data.datasets = renameKey(data.datasets, el, `${el}`);
    }
  });

  return (
    <Grid.Item>
      <Paper elevation={0} variant="outlined" data-testid="youtube-stats-view">
        <EventStatsGraphWrapper>
          <EventStatsHeaderDiv>
            <Inline alignItems="center">
              <div>
                <Text.Body size="xs" isUppercase isBold color="secondary">
                  {`${DateTime.fromISO(startDate).toFormat('MMM dd yyyy')} - \
                ${DateTime.fromISO(endDate).toFormat('MMM dd yyyy')}`}
                </Text.Body>
                <DropDownWrapper>
                  <Select
                    size="m"
                    value={dateRangeOption?.value}
                    options={dateRangeOptions}
                    handleChange={(newValue) => {
                      if (newValue[0].value) {
                        handleDateRangeChange(newValue);
                      }
                    }}
                  />
                </DropDownWrapper>
                {isDatePickerOpen && (
                  <Modal width="fit-content">
                    <Surface padding={'0'}>
                      <ModalHeader titleText="Pick custom date range" closeButton onClose={() => closeDatePicker()} />
                      <DateRange
                        ranges={[selectionRange]}
                        editableDateInputs
                        onChange={handleDateRangeSelect}
                        minDate={viewAllData ? LIFE_TIME_DATE_LOCAL.toJSDate() : START_DATE_LOCAL.toJSDate()}
                      />
                    </Surface>
                  </Modal>
                )}
              </div>
              <ViewEvents
                dateRangeOptions={dateRangeOptions}
                dateRangeSelection={dateRangeOption}
                page={DESTINATION_TYPES.YOUTUBE}
              />
            </Inline>
            <Inline justifyContent="center" alignItems="center">
              <NavTabs onChange={handleDataPageChange} value={dataPage}>
                {eventId !== null ? (
                  <NavTab
                    data-testid="youtube-views-tab"
                    icon={<IconWrapper className="fa fa-video-camera" />}
                    label="Live Views Minute by Minute"
                  />
                ) : (
                  <NavTab
                    data-testid="youtube-views-tab"
                    icon={<IconWrapper className="fa fa-video-camera" />}
                    label="Live Views"
                  />
                )}
                <NavTab data-testid="youtube-analytics-views-tab" icon={<TeamOutlined />} label="Views" />
                <NavTab data-testid="youtube-avgWatchtime-tab" icon={<ClockCircleOutlined />} label="Avg Watch Time" />
              </NavTabs>
            </Inline>
            <Inline justifyContent="flex-end" alignItems="center">
              <ExportCSVWrapper>
                <>
                  <Tooltip
                    title={
                      <h6>
                        {isExportCSVLoading
                          ? `Please wait, your file is being prepared.`
                          : `Download all data as CSV.`}
                      </h6>
                    }
                    arrow
                    placement="top"
                  >
                    <Link
                      component="button"
                      underline="none"
                      variant="body2"
                      disabled={isExportCSVLoading}
                      onClick={exportCSV}
                    >
                      <LinkButtonContainer>
                        <ExportOutlined />
                        <span style={{ marginLeft: '5px' }}>Export CSV</span>
                      </LinkButtonContainer>
                    </Link>
                  </Tooltip>
                  {isExportCSVLoading ? <FillBlockSpinner fontSize={LOADER_SIZE_SUB_COMPONENT} /> : null}
                </>
              </ExportCSVWrapper>
            </Inline>
          </EventStatsHeaderDiv>
          <TabsDiv>
            {dataPageName !== PAGE_VALUE.LIVE_VIEWS ? (
              <ContainedTabs onChange={handleGroupByChange} value={dataType}>
                <ContainedTab data-testid="embed-stats-view-none-tab" label="None" />
                <ContainedTab data-testid="embed-stats-view-eventType-tab" label="Event Type" />
                <ContainedTab
                  disabled={disableDeviceTab}
                  data-testid="embed-stats-view-deviceType-tab"
                  label="Device Type"
                />
              </ContainedTabs>
            ) : (
              <ContainedTabs style={{ width: '215px' }}></ContainedTabs>
            )}

            <Tooltip title={<h6>{ZOOM_CTRL_TOOLTIP}</h6>} arrow placement="top">
              <ChartControls>
                <ContainedButton id="zoomPlus">
                  <ZoomInOutlined style={{ fontSize: '20px' }} />
                </ContainedButton>
                <ContainedButton id="zoomMinus">
                  <ZoomOutOutlined style={{ fontSize: '20px' }} />
                </ContainedButton>
                <ContainedButton id="reset-zoom">
                  <UndoOutlined style={{ fontSize: '20px' }} />
                </ContainedButton>
              </ChartControls>
            </Tooltip>
            {eventId && dataPageName === PAGE_VALUE.LIVE_VIEWS ? (
              <ContainedTabs style={{ width: '154px' }}></ContainedTabs>
            ) : (
              <ContainedTabs onChange={handleTimeWindowChange} value={timeWindow}>
                <ContainedTab data-testid="youtube-day-tab" label="Day" />
                <ContainedTab disabled={disableTab} data-testid="youtube-week-tab" label="Week" />
                <ContainedTab disabled={disableTab} data-testid="youtube-month-tab" label="Month" />
              </ContainedTabs>
            )}
          </TabsDiv>
          <GraphWrapper data-testid="youtube-stats-view-lineChart">
            {isInfoBoxDisplayed && dataPageName !== PAGE_VALUE.LIVE_VIEWS && (
              <InfoBoxWrapper>
                <InfoBox
                  closeButton
                  infoText={
                    'Historical data will be available after 2-3 days of live streaming. This is due to the limitation of Youtube API.'
                  }
                  onClose={() => setInfoBoxDisplayStatus(false)}
                />
              </InfoBoxWrapper>
            )}
            {isChartLoading ? (
              <FillBlockSpinner fontSize={LOADER_SIZE_COMPONENT} />
            ) : (
              <LineChart data={data} dataPageName={dataPageName} tooltipLabel={TOOLTIP_LABEL.VIEWS} />
            )}
          </GraphWrapper>
        </EventStatsGraphWrapper>
      </Paper>
    </Grid.Item>
  );
};

YoutubeStatsGraphView.defaultProps = {
  data: {
    labels: ['Aug 9', 'Sep 1', 'Sep 2', 'Sep 9', 'Sep 15', 'Oct 1', 'Oct 8'],
    datasets: { data: [12, 7, 14, 30, 11, 12, 4, 79, 13, 26, 12, 21, 10] },
  },
};

YoutubeStatsGraphView.propTypes = {
  isChartLoading: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(
    PropTypes.exact({
      labels: PropTypes.arrayOf(PropTypes.string).isRequired,
      datasets: PropTypes.arrayOf(
        PropTypes.objectOf(
          PropTypes.exact({
            data: PropTypes.arrayOf(PropTypes.number).isRequired,
            label: PropTypes.string.isRequired,
          })
        )
      ),
    })
  ).isRequired,
  dateRangeOptions: PropTypes.arrayOf(
    PropTypes.exact({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number.isRequired]).isRequired,
    }).isRequired
  ).isRequired,
  dateRangeDropdownValue: PropTypes.exact({
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number.isRequired]).isRequired,
  }).isRequired,
  handleDateRangeChange: PropTypes.func.isRequired,
  disableDayTab: PropTypes.bool.isRequired,
  dataPageName: PropTypes.string.isRequired,
  handleTimeWindowChange: PropTypes.func.isRequired,
  timeWindow: PropTypes.number.isRequired,
  exportCSV: PropTypes.func.isRequired,
  isExportCSVLoading: PropTypes.bool.isRequired,
  isDatePickerOpen: PropTypes.bool.isRequired,
  closeDatePicker: PropTypes.func.isRequired,
  handleCustomDateRangeSelection: PropTypes.func.isRequired,
};

YoutubeStatsGraphView.displayName = 'YoutubeStatsGraphView';

export default YoutubeStatsGraphView;
